import { useMemo } from "react";

import type { UnactivatedMember } from "@storyboard/web-client";
import { useReactiveSelectQuery } from "@/models/hooks/useReactiveSelectQuery";
import { driverRosterQuery, driverTotalCountQuery, driverVolatileQuery } from "@/models/repositories/Driver";
import type { DriverFilterConfig, DriverRow, TagDefinition } from "@/types/driver";
import { dedupeDriversByMembership } from "./dedupeDriversByMembership";
import { applyLocationFilter, applySortFromVolatile } from "./driverSort";
import { applyTagFilters, applyTagSort } from "./tagFilters";
import { unactivatedMemberMatchesSearch } from "./unactivatedMemberSearch";

const VOLATILE_THROTTLE_MS = 250;
const ROSTER_THROTTLE_MS = 750;
const COUNT_THROTTLE_MS = 750;

function displayNameFromUnactivatedMember(m: UnactivatedMember): string | null {
  return m.account?.name ?? m.invite?.name ?? m.invite?.email ?? m.invite?.phoneNumber ?? null;
}

function syntheticDriverRowForUnactivated(
  m: UnactivatedMember,
  workspaceId: string,
  tagValueMap: Map<string, Record<string, string[]>>,
): DriverRow {
  return {
    accountId: m.account?.id ?? null,
    workspaceMembershipId: m.membershipId,
    feedId: null,
    workspaceId,
    name: displayNameFromUnactivatedMember(m),
    alias: null,
    recentMessage: null,
    group: null,
    groupId: null,
    location: null,
    country: null,
    stateProvince: null,
    volume: null,
    feedPrefId: null,
    totalAliasCount: 0,
    subscribedAliasCount: 0,
    unread: null,
    calculatedAvailability: null,
    offline: null,
    mostRecentDrivingStatus: null,
    mostRecentDrivingStatusChangedAt: null,
    mostRecentDutyStatusStatus: null,
    mostRecentDutyStatusChangedAt: null,
    mostRecentHandsFreeStatusEnabled: null,
    mostRecentHandsFreeStatusUpdatedAt: null,
    mostRecentVolumeAudible: null,
    membershipAvailabilityUpdatedAt: null,
    latitude: null,
    longitude: null,
    tags: tagValueMap.get(m.membershipId) ?? {},
  };
}

export function useDriverQuery(
  workspaceId: string,
  membershipId: string | undefined,
  config: DriverFilterConfig,
  tagValueMap: Map<string, Record<string, string[]>>,
  tagFilters: Record<string, string[]>,
  tagSort: { tagId: string; direction: "asc" | "desc" } | null,
  unactivatedMembers: UnactivatedMember[] = [],
  tags: readonly TagDefinition[] = [],
) {
  const rosterResult =
    useReactiveSelectQuery(
      driverRosterQuery,
      [
        {
          workspaceId,
          membershipId: membershipId ?? "",
          myCurrentWorkspaceMembershipId: membershipId ?? "",
          filter: config,
        },
      ],
      "DriverView:driverRosterQuery",
      ROSTER_THROTTLE_MS,
    ) ?? [];

  const volatileResult =
    useReactiveSelectQuery(
      driverVolatileQuery,
      [
        {
          workspaceId,
          membershipId: membershipId ?? "",
          language: config.language,
        },
      ],
      "DriverView:driverVolatileQuery",
      VOLATILE_THROTTLE_MS,
    ) ?? [];

  const totalCountResult =
    useReactiveSelectQuery(
      driverTotalCountQuery,
      [{ workspaceId, membershipId: membershipId ?? "", tab: config.tab, teamAliases: config.teamAliases }],
      "DriverView:driverTotalCountQuery",
      COUNT_THROTTLE_MS,
    ) ?? [];

  const unactivatedLabelByMembershipId = useMemo(() => {
    const m = new Map<string, string>();
    for (const member of unactivatedMembers) {
      const label = displayNameFromUnactivatedMember(member);
      if (label) m.set(member.membershipId, label);
    }
    return m;
  }, [unactivatedMembers]);

  const visibleMembershipIds = useMemo(() => {
    const ids = new Set<string>(rosterResult.map((r) => r.workspaceMembershipId));
    if (
      config.statusFilter.length === 1 &&
      config.statusFilter[0] === "never-logged-in" &&
      unactivatedMembers.length > 0 &&
      config.groupIds.length === 0
    ) {
      for (const m of unactivatedMembers) {
        if (ids.has(m.membershipId)) continue;
        if (
          !unactivatedMemberMatchesSearch(m, config.search, config.searchTargets, tagValueMap.get(m.membershipId), tags)
        )
          continue;
        ids.add(m.membershipId);
      }
    }
    return ids;
  }, [
    rosterResult,
    config.statusFilter,
    config.search,
    config.searchTargets,
    config.groupIds,
    unactivatedMembers,
    tagValueMap,
    tags,
  ]);

  const { drivers, allAliases } = useMemo(() => {
    const perFeedVolatile = new Map(volatileResult.map((v) => [v.feedId, v]));

    const rosterFeedIds = new Set(rosterResult.map((r) => r.feedId).filter((id): id is string => id != null));
    const teamVolatile = volatileResult.filter((v) => rosterFeedIds.has(v.feedId));
    const driverAgg = config.tab === "all" ? aggregateByDriver(teamVolatile) : null;

    const sortMap = new Map<string, { unread: boolean | null; latestItemCreatedAt: string | null }>();

    const merged: DriverRow[] = rosterResult.map((r) => {
      const sortKey = r.feedId ?? r.workspaceMembershipId;
      const feedVol = r.feedId != null ? perFeedVolatile.get(r.feedId) : undefined;
      const agg = driverAgg?.get(r.workspaceMembershipId);

      const unread = driverAgg ? (agg?.unread ?? null) : (feedVol?.unread ?? null);
      const latestItemCreatedAt = driverAgg
        ? (agg?.latestItemCreatedAt ?? null)
        : (feedVol?.latestItemCreatedAt ?? null);
      const recentMessage = driverAgg ? (agg?.recentMessage ?? null) : (feedVol?.recentMessage ?? null);

      sortMap.set(sortKey, { unread, latestItemCreatedAt });

      const apiLabel = unactivatedLabelByMembershipId.get(r.workspaceMembershipId);
      const name = r.name ?? apiLabel ?? null;

      return {
        ...r,
        name,
        recentMessage,
        unread,
        tags: tagValueMap.get(r.workspaceMembershipId) ?? {},
      };
    });

    const dedupedMerged = dedupeDriversByMembership(merged);

    if (
      config.statusFilter.length === 1 &&
      config.statusFilter[0] === "never-logged-in" &&
      unactivatedMembers.length > 0
    ) {
      const have = new Set(dedupedMerged.map((d) => d.workspaceMembershipId));
      const skipSyntheticForGroupFilter = config.groupIds.length > 0;
      for (const m of unactivatedMembers) {
        if (have.has(m.membershipId)) continue;
        if (skipSyntheticForGroupFilter) continue;
        if (
          !unactivatedMemberMatchesSearch(m, config.search, config.searchTargets, tagValueMap.get(m.membershipId), tags)
        )
          continue;
        dedupedMerged.push(syntheticDriverRowForUnactivated(m, workspaceId, tagValueMap));
      }
    }

    const sorted = applySortFromVolatile(dedupedMerged, sortMap, config.sort);
    const locationFiltered = config.location ? applyLocationFilter(sorted, config.location) : sorted;

    const tagFiltered = applyTagFilters(locationFiltered, tagFilters);
    const tagSorted = tagSort ? applyTagSort(tagFiltered, tagSort) : tagFiltered;

    const aliases = [
      ...new Set(
        tagSorted
          .map((d) => d.alias)
          .filter((a): a is string => a != null && a.length > 0)
          .map((a) => a.toLowerCase()),
      ),
    ].sort();

    const filtered =
      config.aliasNames.length === 0
        ? tagSorted
        : tagSorted.filter((d) => d.alias != null && config.aliasNames.includes(d.alias.toLowerCase()));

    return { drivers: filtered, allAliases: aliases };
  }, [
    rosterResult,
    volatileResult,
    config.sort,
    config.tab,
    config.location,
    config.aliasNames,
    config.statusFilter,
    config.search,
    config.searchTargets,
    config.groupIds,
    workspaceId,
    tagValueMap,
    tagFilters,
    tagSort,
    unactivatedLabelByMembershipId,
    unactivatedMembers,
    tags,
  ]);

  const totalDriverCount = totalCountResult.length > 0 ? totalCountResult[0].count : 0;

  return { drivers, allAliases, totalDriverCount, visibleMembershipIds };
}

interface VolatileRow {
  aliasMembershipId: string | null;
  unread: boolean | null;
  latestItemCreatedAt: string | null;
  recentMessage: string | null;
}

interface DriverAggregate {
  unread: boolean;
  latestItemCreatedAt: string | null;
  recentMessage: string | null;
}

function aggregateByDriver(rows: VolatileRow[]): Map<string, DriverAggregate> {
  const map = new Map<string, DriverAggregate>();

  for (const v of rows) {
    if (!v.aliasMembershipId) continue;
    const existing = map.get(v.aliasMembershipId);

    if (!existing) {
      map.set(v.aliasMembershipId, {
        unread: v.unread ?? false,
        latestItemCreatedAt: v.latestItemCreatedAt,
        recentMessage: v.recentMessage,
      });
      continue;
    }

    existing.unread = existing.unread || (v.unread ?? false);
    if (
      v.latestItemCreatedAt &&
      (!existing.latestItemCreatedAt || v.latestItemCreatedAt > existing.latestItemCreatedAt)
    ) {
      existing.latestItemCreatedAt = v.latestItemCreatedAt;
      existing.recentMessage = v.recentMessage;
    }
  }

  return map;
}
